import React from 'react';
import { useParams } from 'react-router-dom';

import { OffsetPaginationParams } from 'types/pagination';

import {
  useTrackableItems,
  UseTrackableItemsParams,
} from '../../hooks/useTrackableItems';
import { useTrackableLists } from '../../hooks/useTrackableLists';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import { TRACKABLE_OBJECT_TYPE_LABELS } from '../../utils';

import {
  DetailView,
  DetailViewHeader,
  DetailViewStatus,
} from 'components/DetailView/DetailView';
import { Loader } from 'components/Loader/Loader';
import { NotFound } from 'views/NotFound/NotFound';
import { Tabs, TabsButton, TabsContent, TabsList } from 'components/Tabs/Tabs';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { DropdownButton } from 'components/DropdownButton/DropdownButton';
import { DropdownButtonOption } from 'components/DropdownButton/DropdownButtonOption';

import { TrackableItemsTable } from '../../components/TrackableItemsTable/TrackableItemsTable';
import { AddTrackableItemDialog } from '../../components/AddTrackableItemDialog';
import { BulkAddTrackableItemsDialog } from '../../components/BulkAddTrackableItemsDialog';
import { CSVImportTrackableItemsDialog } from '../../components/CSVImportTrackableItemsDialog';
import { EditTrackableListForm } from '../../components/EditTrackableListForm';

export function TrackableListDetail() {
  const { trackableListId, applicationID } = useParams<{
    trackableListId: string;
    applicationID: string;
  }>();

  const [isCreatingItem, setIsCreatingItem] = React.useState(false);
  const [isBulkAdding, setIsBulkAdding] = React.useState(false);
  const [isImportingCSV, setIsImportingCSV] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const tagFilters = useTagsFilterParams();
  const [queryFilters, setQueryFilters] = React.useState<
    UseTrackableItemsParams & OffsetPaginationParams
  >({
    page: 1,
    trackable_list_id: trackableListId,
  });

  // Reset state when trackableListId changes
  React.useEffect(() => {
    setQueryFilters({
      page: 1,
      trackable_list_id: trackableListId,
    });
    setSearchQuery('');
    // Close any open dialogs when switching lists
    setIsCreatingItem(false);
    setIsBulkAdding(false);
    setIsImportingCSV(false);
  }, [trackableListId]);

  // Handle search
  const handleSearchInput = React.useCallback((value?: string) => {
    setSearchQuery(value || '');
    if (!value) {
      setQueryFilters((prev) => {
        const { trackable_item_names, ...rest } = prev;
        return { ...rest, page: 1 };
      });
    } else {
      setQueryFilters((prev) => ({
        ...prev,
        trackable_item_names: [value],
        page: 1,
      }));
    }
  }, []);

  // Fetch the trackable list details
  const { data: trackableListsData, isLoading: isListLoading } =
    useTrackableLists({
      params: { trackable_list_ids: trackableListId ? [trackableListId] : [] },
      enabled: Boolean(trackableListId),
    });

  const trackableList = trackableListsData?.data?.[0];

  // Fetch trackable items
  const queryResult = useTrackableItems({
    params: {
      ...tagFilters,
      ...queryFilters,
    },
    keepPreviousData: true,
  });

  if (isListLoading) {
    return <Loader text="Loading trackable list..." />;
  }

  if (!trackableList) {
    return (
      <NotFound
        entity="trackable list"
        returnTo={`/applications/${applicationID}/deploy/trackables`}
      />
    );
  }

  if (!trackableListId) {
    return null;
  }

  const hasFilters =
    Object.keys(queryFilters).length > 1 || // More than just trackable_list_id
    Object.values(tagFilters).some(Boolean);

  return (
    <DetailView
      key={trackableList.id}
      size="default"
      className="flex flex-col overflow-hidden"
    >
      <div className="flex-shrink-0">
        <DetailViewHeader
          entity={trackableList}
          singleQueryKey={['trackable-list']}
          isRenaming={false}
        >
          <DetailViewStatus entity={trackableList}>
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 uppercase">
                  Object Type
                </span>
                <span className="text-sm font-medium">
                  {TRACKABLE_OBJECT_TYPE_LABELS[trackableList.object_type]}
                </span>
              </div>
              {trackableList.description && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 uppercase">
                    Description
                  </span>
                  <span className="text-sm">{trackableList.description}</span>
                </div>
              )}
            </div>
          </DetailViewStatus>
        </DetailViewHeader>
      </div>

      <Tabs
        defaultValue="items"
        className="flex flex-1 flex-col overflow-hidden"
      >
        <TabsList className="flex-shrink-0 pl-4">
          <TabsButton value="items">Items</TabsButton>
          <TabsButton value="settings">Settings</TabsButton>
        </TabsList>

        <TabsContent value="items" className="flex-1 overflow-auto">
          {trackableListId && (
            <>
              {/* Header bar with search and actions */}
              <div className="sticky top-0 z-10 flex items-center gap-4 border-b border-gray-200 bg-gray-100 py-0 pr-4 pl-0 dark:border-gray-700 dark:bg-black">
                {/* Search on the left */}
                <QuerySearch
                  entityName="watchlist item"
                  searchLabel={`Search ${TRACKABLE_OBJECT_TYPE_LABELS[trackableList.object_type].toLowerCase()}s...`}
                  onValueChange={handleSearchInput}
                  value={searchQuery}
                  totalElements={queryResult.data?.total_elements}
                />

                {/* Spacer to push dropdown to the right */}
                <div className="flex-grow" />

                {/* Add dropdown button */}
                <DropdownButton
                  label="Add"
                  size="small"
                  onClick={() => setIsCreatingItem(true)}
                >
                  <DropdownButtonOption onClick={() => setIsCreatingItem(true)}>
                    Add Single Item
                  </DropdownButtonOption>
                  <DropdownButtonOption onClick={() => setIsBulkAdding(true)}>
                    Bulk Add Items
                  </DropdownButtonOption>
                  <DropdownButtonOption onClick={() => setIsImportingCSV(true)}>
                    CSV Import
                  </DropdownButtonOption>
                </DropdownButton>
              </div>

              {/* Table directly without wrapper divs */}
              <TrackableItemsTable
                queryResult={queryResult}
                pageSize={50}
                page={queryFilters.page}
                onPageChange={(page) =>
                  setQueryFilters((previous) => ({ ...previous, page }))
                }
                hasFilters={hasFilters}
                onCreate={() => setIsCreatingItem(true)}
                trackableListId={trackableListId}
                objectType={trackableList?.object_type}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="settings" className="p-6">
          <div className="space-y-6">
            <EditTrackableListForm
              trackableList={trackableList}
              onSuccess={() => {
                // Optionally show a success message or refresh data
              }}
            />
          </div>
        </TabsContent>
      </Tabs>

      {trackableList && (
        <>
          <AddTrackableItemDialog
            trackableList={trackableList}
            open={isCreatingItem}
            onClose={() => setIsCreatingItem(false)}
          />

          <BulkAddTrackableItemsDialog
            trackableList={trackableList}
            open={isBulkAdding}
            onClose={() => setIsBulkAdding(false)}
            onSuccess={() => {
              // Optionally show a success message or refresh data
            }}
          />

          <CSVImportTrackableItemsDialog
            trackableList={trackableList}
            open={isImportingCSV}
            onClose={() => setIsImportingCSV(false)}
            onSuccess={() => {
              // Optionally show a success message or refresh data
            }}
          />
        </>
      )}
    </DetailView>
  );
}
